/**
 * textFit.js
 *
 * Two safeguards for the GSAP SplitText titles in the horizontal-scroll sections:
 *
 * 1. installSplitTextAntiClip() — patches SplitText.create() once so every split
 *    element it produces is tagged with a class (cf-split-char / -word / -line).
 *    Paired with CSS in tailwind.css, this gives those elements `overflow: visible`
 *    plus a net-zero vertical padding so accents/descenders/tall (e.g. Hangul)
 *    glyphs are never clipped by tight line-heights (leading-0 / leading-[78%]).
 *
 * 2. preventOverlap() — measures a set of text blocks and, if any two collide,
 *    shrinks the lower-priority block's font-size step by step until the collision
 *    clears. Keeps the fixed, finely-tuned layout intact (no repositioning).
 */

import { SplitText } from 'gsap/SplitText';

let _installed = false;

// Hangul, kana, Han ideographs, fullwidth forms, CJK symbols/punctuation.
const CJK_RE = /[\u1100-\u11ff\u3000-\u30ff\u3130-\u318f\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef]/;

// Zero-width split point AFTER each CJK glyph (used as a wordDelimiter so a
// space-less CJK run is tokenised per-character for line detection).
const CJK_AFTER = /(?<=[\u1100-\u11ff\u3000-\u30ff\u3130-\u318f\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef])/u;

// Does the SplitText target (selector / element / NodeList / array) contain
// any CJK text? SplitText splits words on spaces, so a space-less CJK run is
// treated as one giant unbreakable "word" — it can't be grouped into separate
// lines, so the whole run gets baked into a single line element that then
// re-wraps. For CJK we instead tokenise per character (see CJK_AFTER).
function targetHasCJK(target) {
    try {
        let text = '';
        if (typeof target === 'string') {
            document.querySelectorAll(target).forEach((el) => { text += el.textContent || ''; });
        } else if (target && target.nodeType) {
            text = target.textContent || '';
        } else if (target && typeof target.length === 'number') {
            for (let i = 0; i < target.length; i++) {
                const el = target[i];
                text += (el && el.textContent) || '';
            }
        }
        return CJK_RE.test(text);
    } catch (e) {
        return false;
    }
}

// True if the given string contains any CJK character.
export function hasCJK(text) {
    return CJK_RE.test(text || '');
}

/**
 * Monkey-patch SplitText.create so that:
 *  - every generated char/word/line element carries a class we can target from
 *    CSS (cf-split-char / -word / -line) for the anti-clip rules, and
 *  - line-splits of CJK text use a per-character word delimiter, so lines are
 *    detected correctly. SplitText forms lines by grouping space-delimited
 *    "words"; a space-less CJK run is one word it can't break across lines, so
 *    it bakes the run into a single line element that then re-wraps.
 * Safe to call multiple times (no-ops after first).
 */
export function installSplitTextAntiClip() {
    if (_installed || !SplitText || typeof SplitText.create !== 'function') return;
    _installed = true;

    const original = SplitText.create.bind(SplitText);
    const append = (existing, added) => ((existing ? existing + ' ' : '') + added);

    SplitText.create = (target, config = {}) => {
        const type = config.type || 'chars,words,lines';
        const merged = {
            ...config,
            charsClass: append(config.charsClass, 'cf-split-char'),
            wordsClass: append(config.wordsClass, 'cf-split-word'),
            linesClass: append(config.linesClass, 'cf-split-line'),
        };
        // CJK has no spaces, so SplitText's default space-based word splitting
        // treats an entire CJK run as ONE unbreakable word — it then can't be
        // grouped into separate lines and the line element re-wraps. Tokenise
        // CJK per character (split AFTER each CJK glyph) so line detection works.
        // Latin runs contain no CJK boundary, so they remain whole words and are
        // never broken mid-word.
        if (type.indexOf('lines') > -1 && !config.wordDelimiter && targetHasCJK(target)) {
            merged.wordDelimiter = { delimiter: CJK_AFTER, replaceWith: '' };
        }
        return original(target, merged);
    };
}

function resolveEl(ref) {
    if (!ref) return null;
    return ref.$el || ref; // support Vue component refs and raw elements
}

function rectsOverlap(a, b, gap = 0) {
    return !(
        a.right <= b.left - gap ||
        a.left >= b.right + gap ||
        a.bottom <= b.top - gap ||
        a.top >= b.bottom + gap
    );
}

/**
 * Measure an element's TRUE glyph bounds. These titles use line-heights well
 * below 1 (leading-0 / leading-[78%]), so the element's layout box is much
 * shorter than the visible text (which overflows it). getBoundingClientRect
 * would therefore report a box that misses real visual overlaps. A DOM Range
 * over the text reports bounds based on the font's ascent/descent metrics,
 * which reflect what's actually painted.
 */
function measureRect(el) {
    try {
        const range = document.createRange();
        range.selectNodeContents(el);
        const r = range.getBoundingClientRect();
        if (r && (r.width || r.height)) return r;
    } catch (e) { /* fall through */ }
    return el.getBoundingClientRect();
}

/**
 * Auto-shrink colliding text blocks until they no longer overlap.
 *
 * @param {Array} refs        Vue refs or elements, ordered HIGHEST priority first.
 *                            When two collide, the later (lower-priority) one shrinks.
 * @param {Object} opts
 * @param {number} opts.gap          Minimum required gap in px (default 2).
 * @param {number} opts.step         Shrink factor per iteration (default 0.94).
 * @param {number} opts.minScale     Don't shrink below this fraction of base size (default 0.5).
 * @param {number} opts.maxIterations Safety cap (default 14).
 */
export function preventOverlap(refs, opts = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Only run on localized (non-English) sites. App.vue adds `is-localized` to
    // <body> for any non-default site; the English base layout is left untouched.
    if (!document.body.classList.contains('is-localized')) return;

    const { gap = 2, step = 0.94, minScale = 0.5, maxIterations = 14 } = opts;

    const els = (refs || []).map(resolveEl).filter(Boolean);
    if (els.length < 2) return;

    // Use each element's CURRENT font-size as the base. These titles are already
    // fitted to their own wrapper by resizeTextToFit() (inline px font-size), so
    // we must compose on top of that rather than reset it — we only shrink
    // further when two blocks genuinely collide.
    const base = els.map((el) => parseFloat(window.getComputedStyle(el).fontSize) || 0);
    const scale = els.map(() => 1);

    for (let iter = 0; iter < maxIterations; iter++) {
        let collided = false;

        for (let i = 0; i < els.length; i++) {
            for (let j = i + 1; j < els.length; j++) {
                const a = measureRect(els[i]);
                const b = measureRect(els[j]);
                if (!rectsOverlap(a, b, gap)) continue;

                collided = true;
                // Shrink the lower-priority element (j), unless it's already at the floor.
                if (scale[j] > minScale && base[j] > 0) {
                    scale[j] = Math.max(minScale, scale[j] * step);
                    els[j].style.fontSize = (base[j] * scale[j]).toFixed(2) + 'px';
                    els[j].dataset.cfFitScale = scale[j].toFixed(3);
                } else if (scale[i] > minScale && base[i] > 0) {
                    // j is maxed out — shrink the higher-priority one as a fallback.
                    scale[i] = Math.max(minScale, scale[i] * step);
                    els[i].style.fontSize = (base[i] * scale[i]).toFixed(2) + 'px';
                    els[i].dataset.cfFitScale = scale[i].toFixed(3);
                }
            }
        }

        if (!collided) break;
    }
}

/**
 * Force every text span inside `el` onto a single line and shrink `el`'s
 * font-size until its content no longer overflows its own width. Used for the
 * Circles overlay text, where longer translations (e.g. Japanese) would
 * otherwise wrap. Measurement uses scrollWidth/clientWidth, which are layout
 * values unaffected by the GSAP zoom transform on the element.
 *
 * @param {Element} el
 * @param {Object}  opts
 * @param {number}  opts.minScale     Floor as a fraction of the native size (default 0.4).
 * @param {number}  opts.step         Shrink factor per iteration (default 0.95).
 * @param {number}  opts.maxIterations Safety cap (default 40).
 */
export function fitSingleLine(el, opts = {}) {
    if (typeof window === 'undefined' || !el) return;
    const { minScale = 0.4, step = 0.95, maxIterations = 40 } = opts;

    // Never let the lines wrap.
    el.querySelectorAll('span').forEach((s) => { s.style.whiteSpace = 'nowrap'; });

    // Re-measure from the native (CSS) font-size each run so it adapts to resize.
    el.style.removeProperty('font-size');
    const base = parseFloat(window.getComputedStyle(el).fontSize) || 0;
    if (!base) return;

    let scale = 1;
    for (let i = 0; i < maxIterations; i++) {
        if (el.scrollWidth <= el.clientWidth + 1) break;
        if (scale <= minScale) break;
        scale *= step;
        // Use !important so this beats the per-locale font-size rules in CSS.
        el.style.setProperty('font-size', (base * scale).toFixed(2) + 'px', 'important');
    }
}

/**
 * Helper function for SplitText to implement custom space preservation settings for type: "chars"
 *
 * @param {Element|string} target
 * @param {Object}         config
 * @returns {SplitText}
 */
export function splitChars(target, type = "chars", config = {}) {
    const isChars = type.indexOf("chars") > -1;
    const NBSP = String.fromCharCode(160);
    const ZWJ = String.fromCharCode(8205);
    const extraConfig = isChars ? {
        reduceWhiteSpace: false,
        // Replace each space with ZWJ + NBSP + ZWJ and split on the ZWJ, so the
        // space survives as its own char element instead of being collapsed.
        prepareText: (text) => text.trim().replace(/\s/g, ZWJ + NBSP + ZWJ),
        wordDelimiter: ZWJ
    } : {};

    const instance = SplitText.create(target, {
        type: type,
        ...extraConfig,
        ...config
    });

    // The NBSP "gap" chars can render zero-width in the condensed display fonts
    // used for these titles (their NBSP glyph has ~0 advance), which joins the
    // words together. Force a visible word-space width on those chars so the
    // gap is preserved regardless of the font's space metrics.
    if (instance && instance.chars) {
        instance.chars.forEach((c) => {
            if ((c.textContent || '') === NBSP) {
                c.style.width = '0.35em';
            }
        });
    }

    return instance;
}

/**
 * Replaces standard spaces and NBSPs in a string with EN space (U+2002)
 * so they render with a proper width in Cyrillic/Russian/alternative fonts.
 *
 * @param {string} text
 * @returns {string}
 */
export function convertSpaces(text) {
    if (typeof text !== 'string') return text;
    // Skip if it looks like a URL, asset path, or route
    if (/^(https?:\/\/|\/assets\/|\/api\/)/.test(text)) return text;
    if (/\.(png|jpe?g|gif|svg|avif|mp4|webm|woff2?|otf|ttf|json)$/i.test(text)) return text;
    if (/^\/[a-zA-Z0-9_-]*$/.test(text)) return text;

    return text.replace(/[ \u00A0]/g, '\u2002');
}

/**
 * Recursively traverses an object and replaces spaces in its string properties.
 *
 * @param {*} obj
 * @returns {*}
 */
export function convertSpacesInObject(obj) {
    if (!obj) return obj;
    if (typeof obj === 'string') {
        return convertSpaces(obj);
    }
    if (Array.isArray(obj)) {
        return obj.map(convertSpacesInObject);
    }
    if (typeof obj === 'object') {
        const result = {};
        for (const [key, value] of Object.entries(obj)) {
            result[key] = convertSpacesInObject(value);
        }
        return result;
    }
    return obj;
}

